import { isUuid } from "@/common/helpers/utils";
import {
  breadcrumbUuidResolvers,
  type BreadcrumbDataMapper,
  type BreadcrumbDisableRule,
} from "@/config/navigation/breadcrumb.config";
import { navigationConfig } from "@/config/navigation/navigation.config";
import type { NavigationItem } from "@/config/navigation/types";
import type { UseQueryResult } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

export type BreadcrumbItemNameResponse = {
  isLoading: boolean;
  error: Error | null;
  data: string | null;
};

export type BreadcrumbDisableContext = {
  value: string;
  previousValues: string[];
  routeItem?: NavigationItem;
  parentRouteItem?: NavigationItem;
  disabledSegments: Set<string>;
  rules: BreadcrumbDisableRule[];
};

export function useBreadcrumbUuidLabel(
  segment: string | undefined,
  uuid: string,
): BreadcrumbItemNameResponse | undefined {
  const { t } = useTranslation("common");
  if (!segment) return undefined;

  for (const resolver of breadcrumbUuidResolvers) {
    const shouldUse = resolver.segments.includes(segment);
    const result = mapQueryResult(resolver.useQuery(shouldUse ? uuid : ""), t, resolver.mapData);
    if (shouldUse) return result;
  }

  return undefined;
}

/**
 * Resolves a UUID path segment to a human-readable entity name using the breadcrumb resolver config.
 * Safe to call unconditionally — always calls all resolver hooks (stable hook count, no early returns before hooks).
 * Returns null while loading or when no resolver matches the segment.
 */
export function useUuidEntityTitle(segment: string, uuid: string): string | null {
  const { t } = useTranslation("common");
  let matchedResult: BreadcrumbItemNameResponse | undefined;

  for (const resolver of breadcrumbUuidResolvers) {
    const isMatch = !!segment && !!uuid && resolver.segments.includes(segment);
    const result = mapQueryResult(resolver.useQuery(isMatch ? uuid : ""), t, resolver.mapData);
    if (isMatch && !matchedResult) {
      matchedResult = result;
    }
  }

  if (!matchedResult || matchedResult.isLoading) return null;
  return matchedResult.data;
}

export function useBreadcrumbItemName(value: string, previousValues: string[]): BreadcrumbItemNameResponse {
  const { t } = useTranslation();
  const lastPreviousValue = previousValues[previousValues.length - 1];
  const isValueUuid = isUuid(value);
  const uuidResult = useBreadcrumbUuidLabel(lastPreviousValue, isValueUuid ? value : "");

  if (!isValueUuid) {
    const routePath = `/${[...previousValues, value].join("/")}`;
    const navigationItem = findNavigationItemByPath(navigationConfig, routePath);
    if (navigationItem) return mapStaticResult(navigationItem.title());

    return mapStaticResult(getStaticBreadcrumbNameByConfig(value, t, navigationConfig));
  }

  if (uuidResult) return uuidResult;
  return mapStaticResult(t("common:Unknown"));
}

export function findNavigationItemByPath(items: NavigationItem[], routePath: string): NavigationItem | undefined {
  const stack: NavigationItem[] = [...items];

  for (let index = 0; index < stack.length; index++) {
    const item = stack[index];
    if (item.routePath === routePath) return item;
    if (item.children?.length) stack.push(...item.children);
  }

  return undefined;
}

function getStaticBreadcrumbNameByConfig(pathItem: string, t: TFunction, items: NavigationItem[]): string {
  for (const item of items) {
    if (item.breadcrumbSegment !== pathItem) continue;
    if (!item.breadcrumbLabel) return pathItem;
    return item.breadcrumbLabel(t);
  }

  return pathItem;
}

export function getStaticBreadcrumbName(pathItem: string, t: TFunction): string {
  return getStaticBreadcrumbNameByConfig(pathItem, t, navigationConfig);
}

export function useLastBreadcrumbTitle(location: string): string | null {
  const { t } = useTranslation();
  const pathParts = location.split("/").filter((part) => part !== "");
  if (pathParts.length === 0) return null;

  const lastPart = pathParts[pathParts.length - 1];
  const routePath = `/${pathParts.join("/")}`;
  const navigationItem = findNavigationItemByPath(navigationConfig, routePath);
  if (navigationItem) return navigationItem.title();
  const staticName = getStaticBreadcrumbNameByConfig(lastPart, t, navigationConfig);
  if (staticName && staticName !== lastPart) return staticName;

  return null;
}

export function isBreadcrumbLinkDisabledByRule({
  value,
  previousValues,
  routeItem,
  parentRouteItem,
  disabledSegments,
  rules,
}: BreadcrumbDisableContext): boolean {
  const lastPreviousValue = previousValues[previousValues.length - 1];
  const parentPreviousValue = previousValues[previousValues.length - 2];
  const isUuidValue = isUuid(value);
  const isConfigDisabled = routeItem?.navigable === false;
  const isUuidDisabledByParent = parentRouteItem?.breadcrumbDisableUuidDetails === true;
  const isUuidDisabledBySegment = lastPreviousValue ? disabledSegments.has(lastPreviousValue) : false;
  const isDisabledByRule = rules.some((rule) => rule.segment === value && rule.parentSegment === parentPreviousValue);

  return (
    isConfigDisabled ||
    (isDisabledByRule && lastPreviousValue !== undefined && isUuid(lastPreviousValue)) ||
    (isUuidValue && lastPreviousValue !== undefined && (isUuidDisabledByParent || isUuidDisabledBySegment))
  );
}

function mapStaticResult(data: string): BreadcrumbItemNameResponse {
  return {
    isLoading: false,
    error: null,
    data,
  };
}

function mapQueryResult(
  data: UseQueryResult<unknown>,
  t: TFunction,
  mapData: BreadcrumbDataMapper,
): BreadcrumbItemNameResponse {
  return {
    isLoading: data.isPending,
    error: data.error ?? null,
    data: data.data ? mapData(data.data, t) : null,
  };
}
